import ExlyModal from "common/Components/ExlyModal";
import { isDeviceIOS } from "common/util";
import constants from "constants/constants";
import React from "react";
import { useRefresh } from "react-admin";
import { LimitedOfferModalContent } from "ui/modules/LimitedOfferModalContent";
import { getOfferLockState } from "ui/modules/LimitedOfferModalContent/utils/offerLockState";
import { OFFER_DIRECTION } from "ui/modules/LimitedOfferModalContent/utils/unifiedOffer";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { is_desktop } from "utils/Utils";
import { useStyles } from "./index.styles";
import { logError } from "utils/error";
import { LTO_EDIT_SESSION_KEY } from "ui/modules/LimitedOfferModalContent/constants";

const CreateOfferModal = ({
  open,
  onClose,
  gst_status,
  onCreateSuccess,
  refreshPageOnCreateSuccess = true,
  showShareModal = true,
  initialDirection,
  ...restProps
}) => {
  const classes = useStyles({ is_desktop });

  const [loading, setLoading] = React.useState(false);
  const child = React.useRef(null);
  const isEdit = !!window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY);
  const editRecord = isEdit
    ? JSON.parse(window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY))
    : null;
  const editDirection =
    editRecord?.metadata?.direction ||
    editRecord?.direction ||
    OFFER_DIRECTION.DISCOUNT;
  const isSurge =
    (initialDirection || editDirection) === OFFER_DIRECTION.SURGE;
  const editBookingCount =
    editRecord?.bookingCount ?? editRecord?.total_bookings ?? 0;
  // Three states from the mockup: create / editing (0 bookings) / locked.
  const { isLocked, primaryLabel } = getOfferLockState({
    isEdit,
    bookingCount: editBookingCount,
  });

  const refresh = useRefresh();
  const { notify } = useNotifications();

  React.useEffect(() => {
    if (!isDeviceIOS() || is_desktop) return;

    let prevScrollPos = window.scrollY;
    document.body.style.marginTop = `${-window.scrollY}px`;
    document.body.style.position = "fixed";
    document.body.style.top = 0;
    document.body.style.bottom = 0;
    document.body.style.left = 0;
    document.body.style.right = 0;

    return () => {
      document.body.style.position = "static";
      document.body.style.top = "auto";
      document.body.style.bottom = "auto";
      document.body.style.left = "auto";
      document.body.style.right = "auto";
      document.body.style.marginTop = 0;
      window.scrollTo(0, prevScrollPos);
    };
  }, []);

  const handleSave = async () => {
    setLoading(true);
    if (child.current) {
      try {
        const response = await child.current.triggerSave();
        if (response && response.listingOfferData) {
          const { limitedOfferCreative } = response;
          if (!limitedOfferCreative) {
            onClose();
            if (refreshPageOnCreateSuccess) refresh();
            if (onCreateSuccess) onCreateSuccess();
          }
        }
      } catch (error) {
        logError({ error });

        notify(constants.error_message, notification_color_keys.error);
      } finally {
        setLoading(false);
      }
    }
  };

  const modalTitle = isEdit
    ? `Edit ${isSurge ? "Surge Pricing Rule" : "Early Bird Offer"}`
    : isSurge
    ? "Create Surge Pricing Rule"
    : "Create Early Bird Offer";

  const primaryBtnText = primaryLabel;

  return (
    <ExlyModal
      modal_props={{
        modalPaperClassName: classes.modalPaperClassName,
      }}
      open={open}
      onClose={onClose}
      title={modalTitle}
      primaryBtnText={primaryBtnText}
      onPrimaryBtnClick={handleSave}
      secondaryBtnText="Cancel"
      onSecondaryBtnClick={onClose}
      primaryBtnProps={{
        disabled: loading || isLocked,
        loading,
      }}
      mobile_modal_props={{
        secondaryAction: {
          label: "Cancel",
          onClick: onClose,
        },
        footerClassName: classes.footerClassName,
      }}
      {...restProps}
    >
      <div className={classes.dialogContentClassName}>
        <LimitedOfferModalContent
          hideLayout
          ref={child}
          handleClick={onClose}
          gst_status={gst_status}
          showShareModal={showShareModal}
          directionPreset={initialDirection}
        />
      </div>
    </ExlyModal>
  );
};

export { CreateOfferModal };
